/**
 * Display labels the backend still sends as German text (DEV-158 replaces them
 * by keys). Until then the UI translates the KNOWN ones here: the German text
 * is the lookup key, anything unknown passes through unchanged — so a label the
 * backend adds later shows up in German instead of disappearing.
 *
 * Keep the keys identical to the strings in
 * app/services/vulnerability_case_service.py (CLOCKS, FACTS, AUTHORITY_TEXT,
 * _LABELS, the member-state hint).
 */

import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

const BACKEND_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  // Reporting clocks (CLOCKS) — the text before " (" is the short form, see shortBackendLabel().
  'Frühwarnung (24 h)': msg`Frühwarnung (24 h)`,
  'Meldung (72 h)': msg`Meldung (72 h)`,
  'Abschlussbericht (14 Tage)': msg`Abschlussbericht (14 Tage)`,
  // Core facts and authority-only fields (FACTS, AUTHORITY_TEXT, _LABELS).
  Zusammenfassung: msg`Zusammenfassung`,
  'Art der Ausnutzung': msg`Art der Ausnutzung`,
  'Auswirkung und Schweregrad': msg`Auswirkung und Schweregrad`,
  'Maßnahmen für Nutzer': msg`Maßnahmen für Nutzer`,
  Korrekturmaßnahmen: msg`Korrekturmaßnahmen`,
  'Begründung der besonderen Umstände': msg`Begründung der besonderen Umstände`,
  'Angaben zum Angreifer': msg`Angaben zum Angreifer`,
  'Sensibilität (TLP)': msg`Sensibilität (TLP)`,
  'Abhilfemaßnahme verfügbar seit': msg`Abhilfemaßnahme verfügbar seit`,
  'Mitgliedstaaten (kein betroffener Kunde mit EU-Land)': msg`Mitgliedstaaten (kein betroffener Kunde mit EU-Land)`,
};

/** The label in the active UI language; unknown labels are returned as they came. */
export function backendLabel(label: string): string {
  const descriptor = BACKEND_LABELS[label];
  return descriptor === undefined ? label : i18n._(descriptor);
}

/** `Frühwarnung (24 h)` → `Frühwarnung`, in the active UI language. */
export function shortBackendLabel(label: string): string {
  return backendLabel(label).split(' (')[0] ?? label;
}

/** A list of backend labels as one comma-separated text. */
export function backendLabelList(labels: readonly string[]): string {
  return labels.map(backendLabel).join(', ');
}
